import { replaceLocaleInPath } from '@ev-tech/search-modules-api';

import { stringifySearchParams } from './searchParams';
import { type GetSearchResultPageUrlProps, prepareFiltersForUrl, removeNonPreservedParams } from './getSearchResultPageUrl';

/**
 * Construct a URL or path to the property search results page based on given filters and options.
 *
 * This is a hybrid function that replaces both {@link getSearchResultPageUrl} and {@link getSearchResultPagePath},
 * providing a unified approach for generating search result links.
 *
 * @function
 * @param {Object} params - The parameters object containing all configuration for generating the search link.
 * @param {string | URL} [params.url] - Optional base URL to which search parameters will be appended. If provided, returns a URL object with preserved params respected. If omitted, returns a path string.
 * @param {Partial<SearchListingsFilters>} params.filters - Filters to apply to the property search. These include things like place information, price range, and property type.
 * @param {GetSearchResultPageUrlOptions} params.options - Search options: language, countryCode etc.
 * @param {(string | RegExp)[]} [params.preservedParams] - Optional array of parameters in the input url that are preserved (e.g. gclid, utm_*). Only used when url is provided.
 * @param {Record<string, unknown>} [params.additionalParams] - Optional additional parameters to include in the search link.
 * @returns {URL | string} A URL object when url parameter is provided, or a path string when url is omitted.
 *
 * @summary Construct a URL or path to the property search results page based on given filters and options.
 *
 */
export function getSearchResultPageLink(params: GetSearchResultPageUrlProps): URL;
export function getSearchResultPageLink(params: Omit<GetSearchResultPageUrlProps, 'url' | 'preservedParams'>): string;

export function getSearchResultPageLink(
  params: Omit<GetSearchResultPageUrlProps, 'url'> & {
    url?: string | URL;
  },
): URL | string {
  const { filters, options, additionalParams = {} } = params;
  const { language, countryCode, pageSize, ...otherOptions } = options;
  const preparedFilters = prepareFiltersForUrl(filters, options);

  if ('url' in params && params.url) {
    const { url, preservedParams } = params;
    let _url = url instanceof URL ? url : new URL(url);
    _url = removeNonPreservedParams(_url, preservedParams || []);
    _url.pathname = replaceLocaleInPath(_url.pathname, language, countryCode);
    const stringifiedParams = stringifySearchParams({ ...preparedFilters, ...otherOptions, ...additionalParams }, _url.searchParams);
    _url.search = stringifiedParams;
    return _url;
  }

  const stringifiedParams = stringifySearchParams({ ...preparedFilters, ...otherOptions, ...additionalParams });
  return `/${countryCode.toLowerCase()}/${language}/propertysearch${stringifiedParams}`;
}
